/**
 * LegalGate — small wrapper that runs after auth is resolved, polls
 * `/legal/me/status`, and renders <LegalReacceptanceModal /> when the
 * current user's accepted versions are out of date with the latest
 * published Terms / Privacy.
 *
 * Skips entirely for unauthenticated visitors and on public legal pages
 * so the modal can never block someone reading /terms or /privacy
 * directly.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import LegalReacceptanceModal from '@/components/legal/LegalReacceptanceModal';

const SKIP_PATHS = ['/terms', '/privacy', '/login', '/register'];

export default function LegalGate() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Reset on auth flips.
    if (!user) { setStatus(null); setDismissed(false); return; }
    if (loading) return;
    let alive = true;
    axios.get(`${API}/legal/me/status`)
      .then(r => { if (alive) setStatus(r.data); })
      .catch(() => { /* network error → silently skip */ });
    return () => { alive = false; };
  }, [user, loading]);

  // Guard rails: only render when authenticated, status loaded, AND
  // the user actually needs re-acceptance, AND we're not on a public
  // legal route (so the modal can never trap users on /terms etc.).
  if (!user || loading || !status?.needs_acceptance || dismissed) return null;
  if (SKIP_PATHS.some(p => pathname.startsWith(p))) return null;

  return (
    <LegalReacceptanceModal
      status={status}
      onAccepted={() => setDismissed(true)}
    />
  );
}
